// Real Construction Management page (replaces the Sprint 0 stub).
//
// Top: project picker.
// KPI row: active phases, critical-path tasks, tasks complete, days to next milestone.
// Center: custom <PhaseRoadmap /> — Linear/Height-style horizontal roadmap.
//   - Phases as group rows (heavier, color-stripe on left)
//   - Tasks as indented child rows (collapsible per phase)
//   - Bars: track + progress fill (gradient), rounded
//   - Critical path: thin red border + red dot in label
//   - Milestones: diamond shape
//   - Today: thin red vertical line + "TODAY" tag
//
// Backend: GET /api/bead-pm/projects/:id/phases  → { phases, tasks, phase_deps, task_deps }
//          GET /api/bead-pm/projects/:id/pm-summary → KPI counts.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import PhaseRoadmap, { RoadmapItem } from '../components/PhaseRoadmap';

const DEMO_PROJECT_ID = '85a46d17-50f3-4bcc-be60-68074efcd5c7';

type PhaseRow = {
  id: string;
  project_id: string;
  parent_phase_id: string | null;
  template_code: string | null;
  name: string;
  phase_type: 'preconstruction' | 'procurement' | 'construction' | 'commissioning' | 'launch' | 'ongoing';
  display_order: number;
  planned_start: string | null;
  planned_end: string | null;
  actual_start: string | null;
  actual_end: string | null;
  status: 'planned' | 'active' | 'blocked' | 'complete' | 'cancelled';
  pct_complete: number;
  notes: string | null;
};

type TaskRow = {
  id: string;
  phase_id: string;
  parent_task_id: string | null;
  name: string;
  task_type: string | null;
  display_order: number;
  planned_start: string | null;
  planned_end: string | null;
  actual_start: string | null;
  actual_end: string | null;
  status: string;
  pct_complete: number;
  is_milestone: boolean;
  is_on_critical_path: boolean;
  assigned_crew_id: string | null;
  work_order_id: string | null;
  notes: string | null;
};

type ObligationLite = {
  id: string;
  catalog_id: string;
  phase_id: string | null;
  due_date: string;
  status: string;
  code: string;
  name: string;
  category: string;
  category_label: string;
  cadence_rule: string;
};

type PmSummary = {
  phase_count: number;
  phases_active: number;
  phases_complete: number;
  task_count: number;
  tasks_complete: number;
  critical_path_tasks: number;
  next_phase_start: string | null;
  next_phase_end: string | null;
};

const PHASE_FILL: Record<string, string> = {
  preconstruction: '#3b82f6',
  procurement:     '#f59e0b',
  construction:    '#10b981',
  commissioning:   '#8b5cf6',
  launch:          '#ec4899',
  ongoing:         '#6b7280',
};

function rowsToRoadmapItems(phases: PhaseRow[], tasks: TaskRow[]): RoadmapItem[] {
  const out: RoadmapItem[] = [];
  const tasksByPhase = new Map<string, TaskRow[]>();
  for (const t of tasks) {
    if (!tasksByPhase.has(t.phase_id)) tasksByPhase.set(t.phase_id, []);
    tasksByPhase.get(t.phase_id)!.push(t);
  }
  for (const p of phases) {
    if (!p.planned_start || !p.planned_end) continue;
    out.push({
      id: p.id,
      template_code: p.template_code,
      name: p.name,
      start: new Date(p.planned_start),
      end: new Date(p.planned_end),
      progress: p.pct_complete,
      status: p.status,
      phase_type: p.phase_type,
      kind: 'phase',
    });
    const phaseTasks = (tasksByPhase.get(p.id) ?? []).sort((a, b) => a.display_order - b.display_order);
    for (const t of phaseTasks) {
      if (!t.planned_start || !t.planned_end) continue;
      out.push({
        id: t.id,
        parent_id: p.id,
        template_code: null,
        name: t.name,
        start: new Date(t.planned_start),
        end: new Date(t.planned_end),
        progress: t.pct_complete,
        status: t.status,
        is_milestone: t.is_milestone,
        is_critical_path: t.is_on_critical_path,
        phase_type: p.phase_type,
        kind: 'task',
      });
    }
  }
  return out;
}

function KpiCard({ label, value, tone, sub }: { label: string; value: string; tone?: string; sub?: string }) {
  const color =
    tone === 'good' ? '#10b981' :
    tone === 'warn' ? '#f59e0b' :
    tone === 'bad'  ? '#ef4444' : 'var(--text)';
  return (
    <div className="card">
      <div className="card__body" style={{ padding: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

type ViewMode = '6mo' | '1yr' | 'all';

function viewWindow(mode: ViewMode, today = new Date()): { start?: Date; end?: Date } {
  if (mode === 'all') return {};
  const months = mode === '6mo' ? 6 : 12;
  const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);            // 1 month back
  const end   = new Date(today.getFullYear(), today.getMonth() + months, 0);       // N months forward
  return { start, end };
}

// Smaller KPI used inside the Build Readiness panel — sized for an in-panel
// 5-up grid, with a hint-text line for context.
function ReadyKpi({ label, value, tone, sub }: { label: string; value: any; tone?: 'good' | 'warn' | 'bad'; sub?: string }) {
  const color =
    tone === 'good' ? '#10b981' :
    tone === 'warn' ? '#f59e0b' :
    tone === 'bad'  ? '#ef4444' : '#0f172a';
  return (
    <div style={{ padding: '10px 12px', borderRadius: 4, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color, marginTop: 2, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function ConstructionPage() {
  const [projectId, setProjectId] = useState(DEMO_PROJECT_ID);
  const [viewMode, setViewMode]   = useState<ViewMode>('6mo');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: projects = [] } = useQuery<any[]>({
    queryKey: ['bead-projects-pm'],
    queryFn: () => api.get('/bead/projects'),
  });

  const { data, isLoading } = useQuery<{ phases: PhaseRow[]; tasks: TaskRow[]; phase_deps: { phase_id: string; depends_on_id: string }[] }>({
    queryKey: ['pm-phases', projectId],
    queryFn: () => api.get(`/bead-pm/projects/${projectId}/phases`),
    enabled: !!projectId,
  });

  const { data: summary } = useQuery<PmSummary>({
    queryKey: ['pm-summary', projectId],
    queryFn: () => api.get(`/bead-pm/projects/${projectId}/pm-summary`),
    enabled: !!projectId,
  });

  const { data: obligations = [] } = useQuery<ObligationLite[]>({
    queryKey: ['pm-obligations', projectId],
    queryFn: () => api.get(`/bead-pm/projects/${projectId}/obligations`),
    enabled: !!projectId,
  });

  // Construction-readiness: which segments are clear to bore, which are
  // blocked by an unresolved permit, which are blocked by procurement.
  // This is the gate that PM teams care about — phase plans don't matter if
  // every segment is blocked by an outstanding §404.
  const { data: readiness } = useQuery<any>({
    queryKey: ['construction-readiness', projectId],
    queryFn: () => api.get(`/permits/projects/${projectId}/construction-readiness`),
    enabled: !!projectId,
    staleTime: 10_000,
  });

  const phases = data?.phases ?? [];
  const tasks  = data?.tasks  ?? [];
  const items  = useMemo(() => rowsToRoadmapItems(phases, tasks), [phases, tasks]);

  // Resolve `selected` from selectedId on every render — auto-syncs with refetched data.
  const selected = useMemo(
    () => (selectedId ? items.find(i => i.id === selectedId) ?? null : null),
    [selectedId, items],
  );

  const selectedProject = projects.find((p: any) => p.id === projectId);

  const daysToNextMilestone = (() => {
    if (!summary?.next_phase_end) return '—';
    const ms = new Date(summary.next_phase_end).getTime() - Date.now();
    const days = Math.ceil(ms / 86_400_000);
    return days >= 0 ? String(days) : '—';
  })();

  return (
    <>
      <div className="page__header">
        <div>
          <h1 className="page__title">Construction Management</h1>
          <div className="page__subtitle">
            {selectedProject
              ? `${selectedProject.project_code} · ${selectedProject.project_name}${selectedProject.county ? ' · ' + selectedProject.county : ''}${selectedProject.state ? ' · ' + selectedProject.state : ''}`
              : 'Project lifecycle, phases, tasks, critical path'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={projectId} onChange={e => setProjectId(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'white', fontSize: 13, minWidth: 220 }}>
            {projects.map((p: any) => (
              <option key={p.id} value={p.id}>{p.project_code} — {p.project_name} ({p.state ?? '—'})</option>
            ))}
          </select>
          <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', border: '1px solid var(--border)' }}>
            {([
              { v: '6mo', l: '6 mo' },
              { v: '1yr', l: '1 yr' },
              { v: 'all', l: 'All' },
            ] as const).map(o => (
              <button key={o.v} onClick={() => setViewMode(o.v)}
                style={{
                  padding: '6px 12px',
                  background: viewMode === o.v ? '#0f172a' : 'white',
                  color: viewMode === o.v ? 'white' : 'var(--text)',
                  border: 0, fontSize: 12, cursor: 'pointer', fontWeight: 600,
                }}>{o.l}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <KpiCard label="Active phases" value={String(summary?.phases_active ?? 0)} sub={`${summary?.phase_count ?? 0} total`} />
        <KpiCard label="On critical path" value={`${summary?.critical_path_tasks ?? 0} tasks`} tone="warn" />
        <KpiCard label="Tasks complete" value={`${summary?.tasks_complete ?? 0} of ${summary?.task_count ?? 0}`} />
        <KpiCard label="Days to next milestone" value={daysToNextMilestone} tone="good" sub={summary?.next_phase_end ? new Date(summary.next_phase_end).toLocaleDateString() : ''} />
      </div>

      {/* ──── Build-readiness gate ──────────────────────────────────────────
          Segment-level "can we actually start construction?" check. Pulls
          permit and procurement state and turns it into actionable counts.
          This is the page's most important guardrail — phase plans are
          aspirational until every covering permit is issued and procurement
          is wrapped. */}
      {readiness && (
        <div style={{
          marginBottom: 16, padding: 16, borderRadius: 6,
          background: 'white', border: '1px solid var(--border)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
              Build readiness
              <span style={{ fontSize: 11, fontWeight: 400, color: '#64748b', marginLeft: 8 }}>
                Can a crew actually start work today?
              </span>
            </div>
            <Link to="/permits" style={{ fontSize: 11, color: '#1e3a8a', textDecoration: 'none' }}>
              Manage permits →
            </Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
            <ReadyKpi label="Segments total"    value={readiness.summary.total_segments} />
            <ReadyKpi label="Ready to build"    value={readiness.summary.ready_to_build}
              tone={readiness.summary.ready_to_build > 0 ? 'good' : undefined}
              sub="permits issued + procurement clear" />
            <ReadyKpi label="Blocked by permits" value={readiness.summary.permit_blocked}
              tone={readiness.summary.permit_blocked > 0 ? 'bad' : 'good'} />
            <ReadyKpi label="Blocked by procurement"
              value={readiness.summary.procurement_blocked ? 'YES' : 'no'}
              tone={readiness.summary.procurement_blocked ? 'bad' : 'good'}
              sub={readiness.procurement_blocking?.length
                ? `${readiness.procurement_blocking.length} pending`
                : 'all clear'} />
            <ReadyKpi label="In construction"   value={readiness.summary.in_construction}
              sub={`${readiness.summary.completed} completed`} />
          </div>

          {/* Top blocking permits (the actual punchlist) */}
          {readiness.permits_blocking?.length > 0 && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
                Permits blocking the most segments
              </div>
              {readiness.permits_blocking.slice(0, 5).map((p: any) => (
                <Link key={p.permit_id} to="/permits"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '6px 8px', fontSize: 12,
                    borderBottom: '1px solid #f8fafc',
                    textDecoration: 'none', color: '#0f172a',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <span>
                    <span style={{
                      display: 'inline-block', padding: '1px 6px', fontSize: 9, fontWeight: 700,
                      background: p.status === 'denied' ? '#fecaca' : '#fef3c7',
                      color: p.status === 'denied' ? '#7f1d1d' : '#92400e',
                      borderRadius: 3, marginRight: 6, textTransform: 'uppercase',
                    }}>{p.status.replace(/_/g, ' ')}</span>
                    {p.kind.replace(/_/g, ' ')}
                    {p.feature_owner && <span style={{ color: '#64748b' }}> · {p.feature_owner}</span>}
                  </span>
                  <span style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>
                    blocks {p.segments_blocked} segment{p.segments_blocked === 1 ? '' : 's'}
                  </span>
                </Link>
              ))}
            </div>
          )}

          {readiness.procurement_blocking?.length > 0 && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
                Procurement still pending
              </div>
              {readiness.procurement_blocking.slice(0, 5).map((p: any) => (
                <div key={p.event_id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '6px 8px', fontSize: 12,
                }}>
                  <span>
                    <span style={{
                      display: 'inline-block', padding: '1px 6px', fontSize: 9, fontWeight: 700,
                      background: '#fef3c7', color: '#92400e',
                      borderRadius: 3, marginRight: 6, textTransform: 'uppercase',
                    }}>{p.status.replace(/_/g, ' ')}</span>
                    {p.event_code}
                  </span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>{p.description}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Phase-color legend */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 12,
        fontSize: 11, color: '#64748b',
        padding: '8px 0',
      }}>
        {Object.entries(PHASE_FILL).map(([k, color]) => (
          <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, background: color, borderRadius: 2, display: 'inline-block' }} />
            <span style={{ textTransform: 'capitalize', fontWeight: 500 }}>{k}</span>
          </span>
        ))}
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <span style={{ width: 6, height: 6, background: '#ef4444', borderRadius: 999, display: 'inline-block' }} />
          <span style={{ fontWeight: 500 }}>Critical path</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 10, background: 'white', border: '2px solid #6b7280', transform: 'rotate(45deg)', display: 'inline-block' }} />
          <span style={{ fontWeight: 500 }}>Milestone</span>
        </span>
      </div>

      {isLoading && (
        <div className="card"><div className="card__body" style={{ color: 'var(--text-muted)' }}>Loading project schedule…</div></div>
      )}
      {/* Click hint */}
      <div style={{
        fontSize: 12, color: '#1e40af', marginBottom: 12,
        padding: '8px 12px',
        background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 4,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ fontSize: 14 }}>→</span>
        <span><b>Click any bar → Edit → change planned end date → Save.</b> Downstream planned phases auto-shift via dependency edges (active phases stay put).</span>
      </div>

      {!isLoading && (() => {
        const win = viewWindow(viewMode);
        return (
          <PhaseRoadmap
            items={items}
            viewStart={win.start}
            viewEnd={win.end}
            onItemClick={(it) => setSelectedId(it.id)}
          />
        );
      })()}

      {/* Selected detail card — editable */}
      {selected && (
        <DetailCard
          key={selected.id}
          selected={selected}
          projectId={projectId}
          obligations={obligations}
          allPhases={phases}
          phaseDeps={data?.phase_deps ?? []}
          onClose={() => setSelectedId(null)}
        />
      )}
    </>
  );
}

// ─── DetailCard ─────────────────────────────────────────────────────
// Read-only view by default. Click "Edit" → fields become inputs.
// Save → PUT /bead-pm/projects/:id/phases/:id (or /tasks/:id).
// React Query invalidates pm-phases + pm-summary → roadmap refetches →
// bar visibly slides to its new position via CSS transition.

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 10, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700,
};
const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px', marginTop: 4,
  fontSize: 13, fontFamily: 'inherit',
  border: '1px solid #cbd5e1', borderRadius: 4,
  background: 'white',
};
const PRIMARY_BTN: React.CSSProperties = {
  padding: '6px 16px', fontSize: 13, fontWeight: 600,
  background: '#0f172a', color: 'white',
  border: 0, borderRadius: 4, cursor: 'pointer',
};
const SECONDARY_BTN: React.CSSProperties = {
  padding: '6px 16px', fontSize: 13, fontWeight: 600,
  background: 'white', color: '#475569',
  border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer',
};
const EDIT_BTN: React.CSSProperties = {
  padding: '6px 16px', fontSize: 13, fontWeight: 600,
  background: '#3b82f6', color: 'white',
  border: '1px solid #3b82f6', borderRadius: 4, cursor: 'pointer',
};

const STATUS_OPTIONS = [
  { v: 'planned',   l: 'Planned'   },
  { v: 'active',    l: 'Active'    },
  { v: 'blocked',   l: 'Blocked'   },
  { v: 'complete',  l: 'Complete'  },
  { v: 'cancelled', l: 'Cancelled' },
];

interface DetailCardProps {
  selected: RoadmapItem;
  projectId: string;
  obligations: ObligationLite[];
  allPhases: PhaseRow[];
  phaseDeps: { phase_id: string; depends_on_id: string }[];
  onClose: () => void;
}

function DetailCard({ selected, projectId, obligations, allPhases, phaseDeps, onClose }: DetailCardProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [cascadedCount, setCascadedCount] = useState(0);
  const [draft, setDraft] = useState({
    planned_start: selected.start.toISOString().slice(0, 10),
    planned_end:   selected.end.toISOString().slice(0, 10),
    status:        selected.status,
    pct_complete:  selected.progress,
  });
  const [savedFlash, setSavedFlash] = useState(false);

  const url = selected.kind === 'phase'
    ? `/bead-pm/projects/${projectId}/phases/${selected.id}`
    : `/bead-pm/projects/${projectId}/tasks/${selected.id}`;

  const mutation = useMutation<any, Error, typeof draft>({
    mutationFn: (payload) => api.put(url, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pm-phases', projectId] });
      queryClient.invalidateQueries({ queryKey: ['pm-summary', projectId] });
      setEditing(false);
      const n = (data && typeof data._cascaded === 'number') ? data._cascaded : 0;
      setCascadedCount(n);
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 6000);
    },
  });

  const startEdit = () => {
    setDraft({
      planned_start: selected.start.toISOString().slice(0, 10),
      planned_end:   selected.end.toISOString().slice(0, 10),
      status:        selected.status,
      pct_complete:  selected.progress,
    });
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
  };

  // Compute the date-shift delta and which downstream planned phases
  // would be cascaded. Used to populate the confirmation panel.
  const oldEndStr = selected.end.toISOString().slice(0, 10);
  const newEndStr = draft.planned_end;
  const deltaDays = (() => {
    const oldD = new Date(oldEndStr + 'T00:00:00').getTime();
    const newD = new Date(newEndStr + 'T00:00:00').getTime();
    return Math.round((newD - oldD) / 86_400_000);
  })();

  const affectedPhases = (() => {
    if (deltaDays === 0) return [] as PhaseRow[];
    const visited = new Set<string>();
    const queue = [selected.id];
    while (queue.length) {
      const cur = queue.shift()!;
      for (const d of phaseDeps) {
        if (d.depends_on_id === cur && !visited.has(d.phase_id)) {
          visited.add(d.phase_id);
          queue.push(d.phase_id);
        }
      }
    }
    return allPhases
      .filter(ph => visited.has(ph.id) && ph.status === 'planned')
      .sort((a, b) => a.display_order - b.display_order);
  })();

  const save = () => {
    if (draft.planned_end < draft.planned_start) {
      alert('End date must be on or after start date.');
      return;
    }
    // If any downstream planned phases would shift, route through preview.
    if (deltaDays !== 0 && affectedPhases.length > 0) {
      setShowConfirm(true);
      return;
    }
    // No cascade impact — save directly.
    mutation.mutate(draft);
  };

  const confirmCascade = () => {
    mutation.mutate(draft);
    setShowConfirm(false);
  };
  const backToEdit = () => setShowConfirm(false);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)',
      zIndex: 100, display: 'flex', justifyContent: 'flex-end',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 520, maxWidth: '100%', background: 'white', height: '100vh',
        overflowY: 'auto', padding: 24,
        boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
        animation: 'slideInRight 0.18s ease-out',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <span>{selected.kind === 'phase' ? 'Phase' : 'Task'} detail</span>
              {selected.is_critical_path && (
                <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 999, fontSize: 10 }}>Critical path</span>
              )}
              {selected.is_milestone && (
                <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 999, fontSize: 10 }}>Milestone</span>
              )}

            </div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6, lineHeight: 1.3 }}>
              {selected.template_code && <span style={{ color: '#94a3b8', marginRight: 6 }}>{selected.template_code}</span>}
              {selected.name}
            </div>
          </div>
          <button onClick={onClose}
            style={{ fontSize: 24, color: 'var(--text-muted)', background: 'transparent', border: 0, cursor: 'pointer', lineHeight: 1, flexShrink: 0 }}>
            ×
          </button>
        </div>

        {/* Action row */}
        <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          {!editing ? (
            <button onClick={startEdit} style={PRIMARY_BTN}>Edit</button>
          ) : showConfirm ? (
            <>
              <button onClick={confirmCascade} disabled={mutation.isPending}
                style={{ ...PRIMARY_BTN, background: '#dc2626', borderColor: '#dc2626' }}>
                {mutation.isPending ? 'Applying…' : `Confirm cascade · shift ${affectedPhases.length} phase${affectedPhases.length === 1 ? '' : 's'}`}
              </button>
              <button onClick={backToEdit} style={SECONDARY_BTN}>← Back to edit</button>
            </>
          ) : (
            <>
              <button onClick={save} disabled={mutation.isPending} style={PRIMARY_BTN}>
                {mutation.isPending ? 'Saving…' : (deltaDays !== 0 && affectedPhases.length > 0 ? 'Review cascade →' : 'Save changes')}
              </button>
              <button onClick={cancel} style={SECONDARY_BTN}>Cancel</button>
              {mutation.isError && (
                <span style={{ color: '#ef4444', fontSize: 12 }}>
                  {(mutation.error as any)?.message ?? 'Save failed'}
                </span>
              )}
            </>
          )}
        </div>

        {/* Cascade confirmation panel */}
        {showConfirm && (
          <div style={{ marginTop: 16, border: '2px solid #f59e0b', borderRadius: 6, background: '#fffbeb' }}>
            <div style={{ padding: 14, borderBottom: '1px solid #fde68a', background: '#fef3c7' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                ⚠ Cascade impact preview
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                Shifting {selected.template_code ? selected.template_code + ' ' : ''}{selected.name} by {deltaDays > 0 ? '+' : ''}{deltaDays} day{Math.abs(deltaDays) === 1 ? '' : 's'} will move {affectedPhases.length} downstream planned phase{affectedPhases.length === 1 ? '' : 's'}.
              </div>
              <div style={{ fontSize: 12, color: '#78350f', marginTop: 6, lineHeight: 1.4 }}>
                Active and completed phases keep their actual dates. Only phases in <b>planned</b> status shift to maintain the dependency chain.
              </div>
            </div>
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              {affectedPhases.map((ph, i) => {
                const oldStart = new Date(ph.planned_start ?? '');
                const oldEnd   = new Date(ph.planned_end   ?? '');
                const newStart = new Date(oldStart.getTime() + deltaDays * 86_400_000);
                const newEnd   = new Date(oldEnd.getTime()   + deltaDays * 86_400_000);
                const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                return (
                  <div key={ph.id} style={{
                    padding: '8px 14px',
                    borderBottom: i < affectedPhases.length - 1 ? '1px solid #fde68a' : 'none',
                    display: 'flex', alignItems: 'center', gap: 10, fontSize: 12,
                  }}>
                    <div style={{ flex: 1, fontWeight: 600 }}>
                      {ph.template_code && <span style={{ color: '#94a3b8', marginRight: 6 }}>{ph.template_code}</span>}
                      {ph.name}
                    </div>
                    <div style={{ color: '#78350f', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                      <s style={{ color: '#a16207', opacity: 0.6, marginRight: 4 }}>
                        {fmt(oldStart)} – {fmt(oldEnd)}
                      </s>
                      <span style={{ color: '#0f172a', fontWeight: 700 }}>
                        → {fmt(newStart)} – {fmt(newEnd)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Saved banner — green, prominent, mentions cascade count */}
        {savedFlash && (
          <div style={{
            marginTop: 16,
            padding: '12px 14px',
            background: '#10b981',
            color: 'white',
            borderRadius: 6,
            fontSize: 13, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 10,
            boxShadow: '0 2px 8px rgba(16,185,129,0.25)',
          }}>
            <span style={{ fontSize: 18 }}>✓</span>
            <span>
              Saved.
              {cascadedCount > 0
                ? ` ${cascadedCount} downstream planned phase${cascadedCount === 1 ? '' : 's'} cascade-shifted to maintain dependency chain.`
                : ' No downstream phases needed shifting (none depend on this phase, or they\'re all active/complete).'
              }
            </span>
          </div>
        )}

        {/* Fields (2-col) */}
        <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 13 }}>
          <FieldDate  label="Planned start"  editing={editing} value={editing ? draft.planned_start : selected.start.toISOString().slice(0,10)} onChange={v => setDraft({ ...draft, planned_start: v })} showShift />
          <FieldDate  label="Planned end"    editing={editing} value={editing ? draft.planned_end   : selected.end.toISOString().slice(0,10)}   onChange={v => setDraft({ ...draft, planned_end: v })} showShift />
          <FieldStatus label="Status"        editing={editing} value={editing ? draft.status        : selected.status}                          onChange={v => setDraft({ ...draft, status: v as any })} />
          <FieldRange  label="Progress"      editing={editing} value={editing ? draft.pct_complete  : selected.progress}                        onChange={v => setDraft({ ...draft, pct_complete: v })} />
        </div>

        {/* Linked obligations — for phases that trigger compliance items. */}
        {(() => {
          const linked = obligations.filter(o => o.phase_id === selected.id);
          if (linked.length === 0) return null;
          const navigate2 = (href: string) => { window.location.href = href; };
          return (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700, marginBottom: 8 }}>
                Triggers {linked.length} compliance obligation{linked.length === 1 ? '' : 's'}
              </div>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 4 }}>
                {linked.slice(0, 6).map((o, i) => (
                  <div key={o.id}
                    onClick={() => navigate2('/bead-compliance/calendar')}
                    style={{
                      padding: '8px 12px',
                      borderBottom: i < Math.min(5, linked.length - 1) ? '1px solid #f1f5f9' : 'none',
                      display: 'flex', alignItems: 'center', gap: 10,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}>
                    <div style={{ width: 100, fontFamily: 'ui-monospace, monospace', fontSize: 10, color: '#94a3b8' }}>{o.code}</div>
                    <div style={{ flex: 1, fontSize: 12 }}>{o.name}</div>
                    <div style={{ fontSize: 10, color: '#64748b' }}>{o.cadence_rule.replace(/_/g, ' ')}</div>
                  </div>
                ))}
                {linked.length > 6 && (
                  <div style={{ padding: '6px 12px', fontSize: 11, color: '#64748b', fontStyle: 'italic', textAlign: 'center', borderTop: '1px solid #f1f5f9' }}>
                    + {linked.length - 6} more in Compliance Calendar →
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Helper text */}
        <div style={{ marginTop: 24, padding: 12, background: '#f8fafc', borderRadius: 4, fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
          {editing
            ? 'Save will update the database, refetch the roadmap, animate the bar to its new position, and cascade-shift any downstream planned phases that depend on this one. Active or completed phases stay put.'
            : 'Click Edit to change planned dates, status, or progress. Use the +7d / +14d / +30d quick-shift buttons in edit mode. Saving cascades through dependency edges to downstream planned phases.'
          }
        </div>
      </div>
    </div>
  );
}

// ─── Tiny field components for the drawer ────────────────────────────

// Add N days to a YYYY-MM-DD string, returning YYYY-MM-DD (local).
function addDays(yyyymmdd: string, days: number): string {
  const m = yyyymmdd.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return yyyymmdd;
  const dt = new Date(+m[1], +m[2] - 1, +m[3]);
  dt.setDate(dt.getDate() + days);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const SHIFT_BTN: React.CSSProperties = {
  padding: '3px 8px', fontSize: 11, fontWeight: 600,
  background: '#f1f5f9', color: '#0f172a',
  border: '1px solid #cbd5e1', borderRadius: 3, cursor: 'pointer',
};

function FieldDate({
  label, editing, value, onChange, showShift = false,
}: {
  label: string; editing: boolean; value: string;
  onChange: (v: string) => void;
  showShift?: boolean;
}) {
  return (
    <div>
      <div style={LABEL_STYLE}>{label}</div>
      {editing ? (
        <>
          <input type="date" value={value} onChange={e => onChange(e.target.value)} style={INPUT_STYLE} />
          {showShift && (
            <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
              {[-7, +7, +14, +30].map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => onChange(addDays(value, d))}
                  title={`Shift ${d > 0 ? '+' : ''}${d} days`}
                  style={SHIFT_BTN}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#dbeafe'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}>
                  {d > 0 ? '+' : ''}{d}d
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div style={{ marginTop: 4, fontSize: 14 }}>{new Date(value + 'T00:00:00').toLocaleDateString()}</div>
      )}
    </div>
  );
}

function FieldStatus({ label, editing, value, onChange }: { label: string; editing: boolean; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <div style={LABEL_STYLE}>{label}</div>
      {editing ? (
        <select value={value} onChange={e => onChange(e.target.value)} style={INPUT_STYLE}>
          {STATUS_OPTIONS.map(o => (
            <option key={o.v} value={o.v}>{o.l}</option>
          ))}
        </select>
      ) : (
        <div style={{ marginTop: 4, fontSize: 14, textTransform: 'capitalize' }}>{value}</div>
      )}
    </div>
  );
}

function FieldRange({ label, editing, value, onChange }: { label: string; editing: boolean; value: number; onChange: (v: number) => void }) {
  return (
    <div>
      <div style={LABEL_STYLE}>{label}</div>
      {editing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <input type="range" min={0} max={100} step={5}
            value={value} onChange={e => onChange(+e.target.value)}
            style={{ flex: 1 }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', width: 40, textAlign: 'right' }}>
            {value}%
          </span>
        </div>
      ) : (
        <div style={{ marginTop: 4, fontSize: 14 }}>{value}%</div>
      )}
    </div>
  );
}
